// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL IDIOMA DE LAS PANTALLAS DEL PACIENTE (B-2259, t523, 27-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El portal, el pre-registro, el consentimiento o el presupuesto que firma, el pago, la encuesta, sus radiografías... no
//  tienen la sesión del equipo, así que no saben por /api/config-cliente si la función «idiomas» está encendida. Lo dice
//  EL SERVIDOR en cada respuesta de esas puertas, con su clínica ya resuelta (middleware/inquilinoPublico y el portal):
//      X-Biango-Idiomas: 1                 la función está encendida para la clínica de este enlace
//      X-Biango-Idioma-Paciente: en        (si se sabe) el idioma de la ficha del paciente
//  Sin esa cabecera no se hace nada: la pantalla sale en castellano como siempre.
//
//  CÓMO SE ENCHUFA, SIN TOCAR LAS PANTALLAS: un interceptor de la respuesta de axios (el de las pantallas públicas y el del
//  portal). La primera respuesta con la cabecera decide el idioma y carga su diccionario ANTES de entregar la respuesta a
//  la pantalla: cuando la pantalla pinta sus datos (su setState), ya pinta en ese idioma. Solo una vez por carga de página.
//
//  QUÉ IDIOMA, por este orden: el que el paciente ha elegido en esta pestaña (elegirIdiomaPublico); el del enlace
//  (?idioma=en, lo pone quien manda el enlace si sabe el del paciente); el de su ficha (la cabecera); el de su navegador
//  (utils/i18n · idiomaParaPaciente: uno que no está, inglés; una lengua de España, castellano).
//
//  Sin React y sin import.meta: lo cargan de verdad las guardias (test-helpers/utilDelFrontend).
import { idiomaParaPaciente, esElegible, idiomaActivo } from './i18n.js'
import { cargarIdioma, ZONAS_PACIENTE } from './i18nCargar.js'

const CABECERA = 'x-biango-idiomas'
const CABECERA_PACIENTE = 'x-biango-idioma-paciente'
const CLAVE_ELEGIDO = 'biango-idioma-paciente'

let _decidido = null     // la promesa de la primera decisión (una por carga de página)
let _idiomaDecidido = null

/** El idioma que se ha decidido en esta página (null: el servidor no ha dicho que la función esté encendida). */
export const idiomaPublicoDecidido = () => _idiomaDecidido

function leerElegido() {
  try { return (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(CLAVE_ELEGIDO)) || null } catch (_) { return null }
}

function idiomaDelEnlace(ubicacion = (typeof location !== 'undefined' ? location : null)) {
  try {
    const p = new URLSearchParams((ubicacion && ubicacion.search) || '')
    return p.get('idioma') || p.get('lang') || null
  } catch (_) { return null }
}

function navegador() {
  try {
    if (typeof navigator === 'undefined') return []
    return Array.isArray(navigator.languages) && navigator.languages.length ? [...navigator.languages] : (navigator.language ? [navigator.language] : [])
  } catch (_) { return [] }
}

/** El idioma de una pantalla del paciente (puro: todo lo que decide entra por argumentos). */
export function decidirIdiomaPublico({ elegido = null, enlace = null, ficha = null, idiomasNavegador = [] } = {}) {
  if (elegido === 'es') return 'es'
  for (const c of [elegido, enlace, ficha]) if (c && esElegible(c)) return c
  return idiomaParaPaciente({ navegador: idiomasNavegador })
}

const cabecera = (resp, nombre) => {
  const h = resp && resp.headers
  if (!h) return null
  const v = typeof h.get === 'function' ? h.get(nombre) : h[nombre]
  return v == null ? null : String(v)
}

/**
 * Lo que hace el interceptor con cada respuesta: si trae la cabecera y aún no se ha decidido, decide y carga (y espera a que
 * esté cargado). Devuelve la promesa de la carga, o null si no hace nada.
 */
export function alResponder(resp, { zonas = ZONAS_PACIENTE } = {}) {
  if (cabecera(resp, CABECERA) !== '1') return null
  if (_decidido) return _decidido
  const idioma = decidirIdiomaPublico({ elegido: leerElegido(), enlace: idiomaDelEnlace(), ficha: cabecera(resp, CABECERA_PACIENTE), idiomasNavegador: navegador() })
  _idiomaDecidido = idioma
  _decidido = idioma === idiomaActivo() ? Promise.resolve(idioma) : cargarIdioma(idioma, { zonas }).catch(() => 'es')
  return _decidido
}

/** Engancha a una instancia de axios (la de por defecto de las pantallas públicas, o la del portal). Una vez. */
export function instalarIdiomaPublico(instancia, opciones = {}) {
  if (!instancia || !instancia.interceptors || instancia.__idiomaPublico) return false
  instancia.__idiomaPublico = true
  // t605 · Y AL REVÉS: decidido un idioma que no es el castellano, cada petición lo dice (X-Biango-Idioma) para que los
  // mensajes que escribe el servidor (un error, un aviso) lleguen en ese idioma (backend/services/i18n · trRes). Sin decidir
  // (la función apagada) o en castellano, la petición sale igual que siempre.
  if (instancia.interceptors.request && typeof instancia.interceptors.request.use === 'function') {
    instancia.interceptors.request.use((cfg) => {
      const i = _idiomaDecidido
      if (cfg && i && i !== 'es') {
        try {
          cfg.headers = cfg.headers || {}
          if (typeof cfg.headers.set === 'function') cfg.headers.set('X-Biango-Idioma', i)
          else cfg.headers['X-Biango-Idioma'] = i
        } catch (_) { /* sin cabeceras: en castellano */ }
      }
      return cfg
    })
  }
  instancia.interceptors.response.use((resp) => {
    const p = alResponder(resp, opciones)
    return p ? p.then(() => resp, () => resp) : resp
  })
  return true
}

/**
 * t605 · ANTES DE ENTRAR (el login del portal). Ahí ninguna petición de la pantalla dice la clínica (entrar es global: se busca
 * por el teléfono o el correo), solo el DOMINIO: /api/branding-publico la resuelve y, con la función encendida para ella, trae
 * la cabecera (backend/routes/brandingPublico). Se decide igual que en las demás (alResponder). Devuelve una promesa con el
 * idioma puesto, o con null si no hay nada que hacer (la función apagada: ninguna cabecera, y la pantalla sale como siempre). La pantalla
 * se vuelve a pintar cuando llega (pages/portal/PortalLogin). `pedir` es para las guardias.
 */
export function idiomaAntesDeEntrar({ pedir = (u) => fetch(u), zonas = ZONAS_PACIENTE } = {}) {
  let r
  try { r = pedir('/api/branding-publico') } catch (_) { return Promise.resolve(null) }
  return Promise.resolve(r).then((resp) => alResponder(resp, { zonas })).catch(() => null)
}

/** El paciente elige otro idioma (o 'es'): se recuerda en esta pestaña y la página se vuelve a cargar en él. */
export function elegirIdiomaPublico(codigo, ventana = (typeof window !== 'undefined' ? window : null)) {
  try { if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(CLAVE_ELEGIDO, codigo || 'es') } catch (_) { /* sin almacenamiento */ }
  try { if (ventana && ventana.location) ventana.location.reload() } catch (_) { /* sin ventana */ }
}

/** ¿Va esta dirección de la app con el idioma del paciente? (las pantallas públicas y el portal; la tele de la sala, no). */
export function esPantallaDelPaciente(ruta) {
  // t603 · B-2443 · por el puente de una clínica con la edición local, la ruta lleva /l/<instalación> delante (utils/puenteLocal).
  const r = String(ruta || '').replace(/^\/l\/[A-Za-z0-9-]{8,64}(?=\/|$)/, '')
  // t617 · y la página de privacidad de cada clínica: «/<direccion>/privacidad».
  return /^\/(intake|consentimiento|presupuesto|pago|encuesta|rx|revision-historia|tablet|rgpd|portal)(\/|$)/.test(r)
    || /^\/[a-z0-9-]+\/privacidad\/?$/.test(r)
}

// Solo para las guardias.
export function _olvidarDecisionPublica() { _decidido = null; _idiomaDecidido = null }
